/* eslint-disable complexity */
import React from 'react';
import PartialForm from './partial-form';

import * as O from '../utils/object';
import Form, { Partial } from '@angellira/form';
import * as validations from '../utils/validations';
import InputDocument, { CPF_MASK, CNPJ_MASK } from './document-input';
import ImageDialog from './image-fullscreen-dialog';

import { Button, Grid, CircularProgress, TextField } from '@material-ui/core';

import DocumentIcon from '@material-ui/icons/ChromeReaderMode';

import InfoConsentForm from './InfoConsentForm';
import { EXAMPLE_CONSENT_FORM_ID as EXAMPLE_ID } from '../config/consentformId';

import {
  driverRGFileBlob,
  driverConsentFormFileBlob,
  requiredFreight,
  fetchQueryFreight
} from '../services/driver';

export const availableFields = [
  'name', 'cpf', 'cnpj', 'father', 'birth', 'mother',
  'naturalness', 'rg', 'rgOrgan', 'rgState'
];

import SearchableSelect from '../components/searchable-select';

export const extract = O.filter(O.onlyProps(availableFields));

export default class PersonForm extends PartialForm {
  static defaultProps = {
    legalPerson: false,
    onDocumentValidation: Function.prototype,
    lockDocument: false,
    freightList: [],
    selectedFreight: null,
    requiredFreightBool: false,
    states: [],
    value: {},
    error: {},
    onFreightSelect: Function.prototype,
  }

  documentInput = (ref, props) => <TextField
    required
    fullWidth
    label={ this.props.legalPerson ? 'CNPJ' : 'CPF' }
    margin="dense"
    inputRef={ref}
    {...props}
  />;

  hasImageAdornment() {
    if (this.state.loading)
      return <CircularProgress size={21} />;

    return this.props.hasPreviousImage
      ? <Button onClick={this.showImage}><DocumentIcon /></Button>
      : undefined;
  }

  hasConsentFormAdornment() {
    if (this.state.loading)
      return <CircularProgress size={21} />;

    return this.props.hasConsentPreviousForm
      ? <Button onClick={this.showConsentForm}><DocumentIcon /></Button>
      : undefined;
  }

  closeImage = () => this.setState({ imageDialogOpen: false });

  closeConsentForm = () => this.setState({ consentFormOpen: false });

  showImage = () => {
    this.setState({ loading: true });

    const loadImage = this.state.fileData
      ? Promise.resolve()
      : driverRGFileBlob(this.props.driverId)
        .then(({ data, headers }) => {
          const reader = new FileReader();

          reader.readAsDataURL(data);
          return new Promise((res, rej) => {
            reader.onload = e => res({ data: e.target.result, mime: headers['content-type'] });
            reader.onerror = e => rej(e);
          });
        }).then(({ data, mime }) => this.setState({
          fileData: data,
          fileMime: mime,
        }));

    return loadImage.then(() => this.setState({ imageDialogOpen: true }))
      .finally(() => this.setState({ loading: false }));

  }

  showConsentForm = () => {
    this.setState({ loading: true });
    const loadImage = this.state.consentFormData
      ? Promise.resolve()
      : driverConsentFormFileBlob(this.props.driverId)
        .then(({ data, headers }) => {
          const reader = new FileReader();

          reader.readAsDataURL(data);
          return new Promise((res, rej) => {
            reader.onload = e => res({ data: e.target.result, mime: headers['content-type'] });
            reader.onerror = e => rej(e);
          });
        }).then(({ data, mime }) => this.setState({
          consentFormData: data,
          consentFormMime: mime,
        }));

    return loadImage.then(() => this.setState({ consentFormOpen: true }))
      .finally(() => this.setState({ loading: false }));

  }

  warningSnack(message) {
    return this.props.enqueueSnackbar(message, { variant: 'warning' });
  }

  componentDidMount() {

    requiredFreight().then(({ data }) => {
      this.setState({ requiredFreightBool: data ? data.required : false });
    });

    if(!this.props.isLegalPerson && !this.props.homologatorId) {
      return this.warningSnack(
        'Selecione o Tipo de Consulta e o Tipo de Motorista para prosseguir .'
      );
    }
    if(!this.props.isLegalPerson) {
      this.warningSnack('Selecione o Tipo de Motorista para prosseguir .');
    }
    if(!this.props.homologatorId) {
      this.warningSnack('Selecione o Tipo de Consulta para prosseguir .');
    }

    // initialize selected freight from props if provided
    if (this.props.selectedFreight) {
      this.setState({ selectedFreight: this.props.selectedFreight });
    }
  }

  handleSelectChange = (value) => {
    if (!value) {
      this.setState({ selectedFreight: null });
      this.props.onFreightSelect(null);
      return;
    }

    this.setState({ selectedFreight: value });
    this.props.onFreightSelect(value);
  };

  render() {
    const {
      legalPerson,
      lockDocument,
      onDocumentValidation,
      states,
      onChangeFile,
      onChangeConsentFormFile,
      compelConsentForm,
      needsValidateType,
      loading,
      driverId,
      hasPreviousImage,
      hasConsentPreviousForm,
      isLegalPerson,
      isActiveForm,
      ...rest
    } = this.props;

    return <Form {...rest} treatError={e => ({ error: !!e, helperText: e })}>
      <Grid container spacing={32}>
        <Grid item sm={6} xs={12}>
          <Partial
            el={TextField}
            required
            pattern={this.props.legalPerson ? validations.legalPerson : validations.naturalPerson}
            fullWidth
            disabled={!isActiveForm}
            label="Nome"
            margin="dense"
            { ...this.defaultHandle('name') }
          />
        </Grid>

        <Grid item sm={6} xs={12}>
          <Partial
            el={InputDocument}
            pattern={validations.document}
            mask={this.props.legalPerson ? CNPJ_MASK : CPF_MASK}
            render={this.documentInput}
            onValidation={onDocumentValidation}
            { ...this.defaultHandle(this.props.legalPerson ? 'cnpj' : 'cpf') }
            disabled={lockDocument || !isActiveForm}
          />
        </Grid>
        {
          !this.props.legalPerson && <>
            <Grid item sm={6} xs={12}>
              <Partial
                el={TextField}
                fullWidth
                required
                pattern={validations.name}
                label="Nome do Pai"
                margin="dense"
                disabled={!isActiveForm}
                { ...this.defaultHandle('father') }
              />
            </Grid>

            <Grid item sm={6} xs={12}>
              <Partial
                el={TextField}
                fullWidth
                required
                type="date"
                pattern={validations.birth}
                label="Data de Nascimento"
                margin="dense"
                disabled={!isActiveForm}
                { ...this.defaultHandle('birth') }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item sm={6} xs={12}>
              <Partial
                el={TextField}
                fullWidth
                required
                pattern={validations.name}
                label="Nome da Mãe"
                margin="dense"
                disabled={!isActiveForm}
                { ...this.defaultHandle('mother') }
              />
            </Grid>

            <Grid item sm={6} xs={12}>
              <Partial
                el={TextField}
                fullWidth
                required
                pattern={validations.requiredString}
                label="Naturalidade"
                margin="dense"
                disabled={!isActiveForm}
                { ...this.defaultHandle('naturalness') }
              />
            </Grid>

            <Grid item sm={6} xs={12}>
              <Partial
                el={TextField}
                fullWidth
                required
                pattern={validations.requiredString}
                label="Número do RG"
                margin="dense"
                disabled={!isActiveForm}
                { ...this.defaultHandle('rg') }
              />
            </Grid>

            <Grid item sm={6} xs={12}>
              <Partial
                el={TextField}
                fullWidth
                required
                pattern={validations.requiredString}
                label="Órgão Emissor do RG"
                margin="dense"
                disabled={!isActiveForm}
                inputProps={{ maxLength: 20 }}
                { ...this.defaultHandle('rgOrgan') }
              />
            </Grid>

            <Grid item sm={6} xs={12}>
              <Partial
                el={TextField}
                fullWidth
                required
                select
                SelectProps={{ native: true }}
                extract={({ target: { value } }) => value}
                pattern={validations.oneOf(this.props.states.map(x => x.id))}
                label="Estado Emissor do RG"
                margin="dense"
                disabled={!isActiveForm}
                { ...this.defaultHandle('rgState') }
              >
                <option key={-1} value=""></option>
                {this.props.states.map(({ name, id }) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </Partial>
            </Grid>
            <Grid item sm={6} xs={12} style={
              this.state.requiredFreightBool ? { display: 'block' } : { display: 'none' }
            }>
              <Partial
                el={SearchableSelect}
                itemToString={(i) => i?.description || i?.name}
                itemUniqueId={(x) => x?.id}
                pattern={validations.requiredString}
                selectedItem={this.state.selectedFreight}
                onChange={(value) => {
                  this.handleSelectChange(value);
                }}
                autoSelectSingleItem={true}
                onAutoSelect={(value) => {
                  this.handleSelectChange(value);
                }}
                suggestions={(searchText) =>
                  fetchQueryFreight(1, searchText)
                    .then(({ data }) => {
                      return data;
                    })
                    .catch(() => [])
                }
                TextFieldProps={{
                  required: this.state.requiredFreightBool,
                  margin: 'dense',
                  label: 'Transportador'
                }}
                disabled={!isActiveForm}
              />
            </Grid>
            <Grid item sm={6} xs={12}>
              <Partial
                el={TextField}
                type="file"
                accept="image/png,image/jpeg,application/pdf"
                required={needsValidateType && !hasPreviousImage}
                fullWidth
                pattern={
                  validations.fileMimes(
                    ['image/jpeg', 'image/png', 'application/pdf'],
                    needsValidateType
                  )
                }
                label="Imagem do RG"
                margin="dense"
                disabled={!isActiveForm}
                onChange={onChangeFile}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                InputProps={{ endAdornment: this.hasImageAdornment() }}
              />
            </Grid>

           { !this.props.isLegalPerson && <>
             <Grid item sm={6} xs={8}>
               <Partial
                 el={TextField}
                 type="file"
                 accept="image/png,image/jpeg,application/pdf"
                 required={!hasConsentPreviousForm && compelConsentForm}
                 fullWidth
                 pattern={
                   validations.fileMimes(
                     ['image/jpeg', 'image/png', 'application/pdf'],
                     !hasConsentPreviousForm && compelConsentForm
                   )
                 }
                 {...this.defaultHandle('consentFormFile')}
                 label="Termo de Consentimento"
                 margin="dense"
                 disabled={!isActiveForm}
                 onChange={onChangeConsentFormFile}
                 InputLabelProps={{ shrink: true }}
                 InputProps={{ endAdornment: this.hasConsentFormAdornment() }}
                 variant="outlined"
                 style={{ zIndex: 0}}
               />
             </Grid>
             <Grid
               item
               sm={5}
               xs={4}
               style={{ display: 'flex', marginTop: '10px', alignItems: 'flex-start' }}
             >
               <Button
                 variant="contained"
                 color='primary'
                 type='button'
                 style={{ textTransform: 'none', height: '90%'}}
                 disabled={!isActiveForm}
                 onClick={() =>
                   window
                     .open(`https://drive.google.com/u/0/uc?id=${EXAMPLE_ID}&export=download`)
                 }
               >
                  Arquivo Padrão
               </Button>
               <InfoConsentForm />
             </Grid>
           </>}
          </>
        }
      </Grid>
      <ImageDialog
        src={this.state.fileData}
        mime={this.state.fileMime}
        open={this.state.imageDialogOpen || false}
        onClose={this.closeImage}
      />
      <ImageDialog
        src={this.state.consentFormData}
        mime={this.state.consentFormMime}
        open={this.state.consentFormOpen || false}
        onClose={this.closeConsentForm}
      />
    </Form>;
  }
}
